import React, { useMemo } from 'react';
import './CyberEffects.css';

/**
 * サイバーグリッド背景
 */
export function GridBackground() {
  return <div className="cyber-grid-bg" />;
}

/**
 * スキャンライン効果
 */
export function Scanlines() {
  return <div className="scanlines" />;
}

/**
 * ビネット効果（画面端を暗く）
 */
export function Vignette() {
  return <div className="vignette" />;
}

/**
 * パーティクルフィールド（浮遊する光の粒子）
 */
export function ParticleField({ count = 20, color = 'pink' }) {
  const particles = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 5}s`,
      duration: `${5 + Math.random() * 10}s`,
      size: `${2 + Math.random() * 4}px`,
    }));
  }, [count]);

  return (
    <div className="particle-field">
      {particles.map(p => (
        <div
          key={p.id}
          className={`particle particle-${color}`}
          style={{
            left: p.left,
            animationDelay: p.delay,
            animationDuration: p.duration,
            width: p.size,
            height: p.size,
          }}
        />
      ))}
    </div>
  );
}

/**
 * ネオンテキスト
 */
export function NeonText({ children, color = 'pink', size = 'lg', className = '' }) {
  return (
    <span className={`neon-text neon-text-${color} neon-size-${size} ${className}`}>
      {children}
    </span>
  );
}

/**
 * グローイングボーダー
 */
export function GlowingBorder({ children, color = 'pink', className = '' }) {
  return (
    <div className={`glowing-border glowing-border-${color} ${className}`}>
      {children}
    </div>
  );
}

/**
 * サイバーボタン（角にアクセント付き）
 */
export function CyberButton({ children, onClick, disabled, variant = 'primary', className = '' }) {
  const hasGlowRotate = className.includes('glow-rotate');

  return (
    <button
      className={`cyber-btn cyber-btn-${variant} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="cyber-btn-corner cyber-btn-corner-tl" />
      <span className="cyber-btn-corner cyber-btn-corner-tr" />
      <span className="cyber-btn-corner cyber-btn-corner-bl" />
      <span className="cyber-btn-corner cyber-btn-corner-br" />
      {hasGlowRotate && (
        <>
          <span className="glow-trail glow-trail-1" />
          <span className="glow-trail glow-trail-2" />
          <span className="glow-trail glow-trail-3" />
          <span className="glow-trail glow-trail-4" />
          <span className="glow-trail glow-trail-5" />
          <span className="glow-trail glow-trail-6" />
          <span className="glow-trail glow-trail-7" />
          <span className="glow-trail glow-trail-8" />
          <span className="glow-trail glow-trail-9" />
          <span className="glow-trail glow-trail-10" />
          <span className="glow-trail glow-trail-11" />
          <span className="glow-trail glow-trail-12" />
          <span className="glow-trail glow-trail-13" />
          <span className="glow-trail glow-trail-14" />
          <span className="glow-trail glow-trail-15" />
          <span className="glow-trail glow-trail-16" />
        </>
      )}
      <span className="cyber-btn-content">{children}</span>
    </button>
  );
}

/**
 * カウントダウン表示（劇的な演出）
 */
export function CountdownDisplay({ number, isOpen = false }) {
  if (isOpen) {
    return (
      <div className="countdown-display countdown-open">
        <span className="countdown-text">OPEN!</span>
      </div>
    );
  }

  return (
    <div className="countdown-display" key={number}>
      <span className="countdown-number">{number}</span>
    </div>
  );
}

/**
 * スロットパネル（回答表示用）
 */
export function SlotPanel({
  char,
  isRevealed,
  isCorrect,
  playerName,
  slotIndex,
  scriptLabel,
  animationDelay = 0,
}) {
  const stateClass = isRevealed
    ? isCorrect
      ? 'slot-correct'
      : 'slot-incorrect'
    : '';

  return (
    <div
      className={`slot-panel ${stateClass} ${isRevealed ? 'slot-revealed' : ''}`}
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      <div className="slot-inner">
        <div className="slot-char">{char}</div>
        <div className="slot-meta">
          <span className="slot-script">{scriptLabel}</span>
        </div>
        <div className="slot-player">{playerName}</div>
      </div>
      <div className="slot-glow" />
    </div>
  );
}

/**
 * 結果バッジ（正解/不正解）
 */
export function ResultBadge({ isPerfect, size = 'md' }) {
  return (
    <div className={`result-badge result-badge-${size} ${isPerfect ? 'result-perfect' : 'result-fail'}`}>
      <span className="result-emoji">{isPerfect ? '🎉' : '😢'}</span>
      <span className="result-text">{isPerfect ? 'PERFECT!' : 'MISS...'}</span>
    </div>
  );
}

/**
 * タイマーリング
 */
export function TimerRing({ timeLeft, maxTime, isDanger = false }) {
  const progress = timeLeft / maxTime;
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <div className={`timer-ring ${isDanger ? 'timer-danger' : ''}`}>
      <svg viewBox="0 0 100 100">
        <circle
          className="timer-ring-bg"
          cx="50"
          cy="50"
          r="45"
          fill="none"
          strokeWidth="6"
        />
        <circle
          className="timer-ring-progress"
          cx="50"
          cy="50"
          r="45"
          fill="none"
          strokeWidth="6"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform="rotate(-90 50 50)"
        />
      </svg>
      <div className="timer-ring-text">{timeLeft}</div>
    </div>
  );
}

/**
 * フラッシュオーバーレイ（画面全体のフラッシュ効果）
 */
export function FlashOverlay({ active, color = 'white' }) {
  if (!active) return null;
  return <div className={`flash-overlay flash-${color}`} />;
}

/**
 * すべての背景エフェクトをまとめたコンポーネント
 */
export function CyberBackground({ showParticles = true, showScanlines = false }) {
  return (
    <>
      <GridBackground />
      <Vignette />
      {showParticles && <ParticleField count={15} color="mixed" />}
      {showScanlines && <Scanlines />}
    </>
  );
}

export default {
  GridBackground,
  Scanlines,
  Vignette,
  ParticleField,
  NeonText,
  GlowingBorder,
  CyberButton,
  CountdownDisplay,
  SlotPanel,
  ResultBadge,
  TimerRing,
  FlashOverlay,
  CyberBackground,
};
